import React, { useState, useEffect } from 'react';
import { Box, Container, IconButton, Typography, Card, CardContent, Grid, Select, MenuItem, FormControl, Chip, Tabs, Tab } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Assignment as AssessmentIcon, Psychology as PsychologyIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAssessmentHistory, getSubjectiveWellBeingHistory } from '../../service/assessment';
import Navbar from '../navbar/index';


const AssessmentInsight = () => {
    const navigate = useNavigate();
    const [historyData, setHistoryData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAssessment, setSelectedAssessment] = useState('subjective');
    

    useEffect(() => {
        const fetchHistory = async () => {
            setLoading(true);
            try {
                const user = JSON.parse(localStorage.getItem('user') || '{}');
                if (user.id) {
                    let response;
                    if (selectedAssessment === 'subjective') {
                        response = await getSubjectiveWellBeingHistory(user.id);
                        console.log('Subjective Well-Being history response:', response);
                    } else {
                        response = await getAssessmentHistory(user.id);
                        console.log('Wellness Index history response:', response);
                    }
                    // Parse the response structure: res.data.data
                    const dataArray = response?.data?.data || [];
                    console.log('Data array:', dataArray);
                    setHistoryData(dataArray);
                } else {
                    setHistoryData([]);
                }
            } catch (error) {
                console.error('Error fetching assessment history:', error);
                setHistoryData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [selectedAssessment]);

   const getTier = (score) => {

    // Subjective Well-Being ranges
    if (selectedAssessment === 'subjective') {

        if (score >= 138) {
            return {
                label: 'Excellent',
                color: '#22c55e'
            };
        }

        if (score >= 102) {
            return {
                label: 'Good',
                color: '#3b82f6'
            };
        }

        if (score >= 66) {
            return {
                label: 'Moderate',
                color: '#f59e0b'
            };
        }

        return {
            label: 'Low',
            color: '#ef4444'
        };
    }

    // Wellness Index ranges
    if (score >= 80) {
        return {
            label: 'Excellent',
            color: '#22c55e'
        };
    }

    if (score >= 60) {
        return {
            label: 'Good',
            color: '#3b82f6'
        };
    }

    if (score >= 40) {
        return {
            label: 'Fair',
            color: '#f59e0b'
        };
    }

    return {
        label: 'Needs Improvement',
        color: '#ef4444'
    };
};

    // Parse history data into assessments with cycles
    const assessments = historyData
    .map((item, index) => ({
        cycle: index + 1,

        score:
            selectedAssessment === 'subjective'
                ? item.total_score || 0
                : item.composite || 0,

        date: item.created_at || new Date().toISOString(),

        question_scores:
            item.question_scores || {},

        emotional: item.emotional || 0,
        mental: item.mental || 0,
        physical: item.physical || 0,
        social: item.social || 0,
        financial: item.financial || 0
    }))
    .filter(item => item.score > 0)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map((item, index) => ({
        ...item,
        cycle: index + 1
    }))
    .reverse();

    const assessmentTypes = [
        { value: 'subjective', label: 'Subjective Well-Being', icon: <PsychologyIcon />, color: '#22c55e' },
        { value: 'wellness', label: 'Wellness Index', icon: <AssessmentIcon />, color: '#8b5cf6' }
    ];

    const currentAssessmentType = assessmentTypes.find(t => t.value === selectedAssessment);

    const handleTabChange = (event, newValue) => {
        setSelectedAssessment(newValue);
       
    };

    

    if (loading) {
        return (
            <>
                <Navbar />
                <Box sx={{ p: 3, textAlign: 'center', pt: 16 }}>
                    <Typography variant="h6">Loading assessment insights...</Typography>
                </Box>
            </>
        );
    }

    return (
        <>
            <Navbar />
            <Box sx={{ background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)', minHeight: '100vh', pt: 2 }}>
                <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Header */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                        <Typography variant="h6" sx={{
                            fontWeight: 700,
                            color: '#0B2D6B',
                            background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                        }}>
                            Assessment Insights
                        </Typography>
                        <IconButton
                            onClick={() => navigate(-1)}
                            sx={{
                                backgroundColor: '#F0F5FF',
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                color: '#0B2D6B',
                                '&:hover': {
                                    backgroundColor: '#E0EBFF'
                                }
                            }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Assessment Type Tabs */}
                <Box sx={{ mb: 4 }}>
                    <Card sx={{
                        borderRadius: 3,
                        border: '2px solid #e0e0e0',
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
                    }}>
                        <CardContent sx={{ p: 2 }}>
                            <Tabs
                                value={selectedAssessment}
                                onChange={handleTabChange}
                                sx={{
                                    '& .MuiTab-root': {
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        color: '#64748b',
                                        minHeight: 56,
                                        flex: 1,
                                    },
                                    '& .MuiTab-root.Mui-selected': {
                                        color: selectedAssessment === 'subjective' ? '#22c55e' : '#8b5cf6',
                                    },
                                    '& .MuiTabs-indicator': {
                                        backgroundColor: selectedAssessment === 'subjective' ? '#22c55e' : '#8b5cf6',
                                        height: 3,
                                        borderRadius: '3px 3px 0 0',
                                    },
                                }}
                            >
                                {assessmentTypes.map((type) => (
                                    <Tab
                                        key={type.value}
                                        value={type.value}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Box sx={{ color: type.color }}>{type.icon}</Box>
                                                {type.label}
                                            </Box>
                                        }
                                    />
                                ))}
                            </Tabs>
                        </CardContent>
                    </Card>
                </Box>

                {/* Cycle Cards */}
                {assessments.length === 0 ? (
                    <Card sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '2px solid #e0e0e0' }}>
                        <AssessmentIcon sx={{ fontSize: 80, color: '#94a3b8', mb: 3 }} />
                        <Typography variant="h6" sx={{ color: '#64748b', mb: 2, fontWeight: 600 }}>
                            No Assessment Data
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#94a3b8' }}>
                            You haven't taken any {currentAssessmentType.label} assessments yet.
                        </Typography>
                    </Card>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {assessments.map((assessment) => {
                            
                            const tier = getTier(assessment.score);
                            return (
                                <Card
                                    key={assessment.cycle}
                                 
                                    sx={{
                                        borderRadius: 3,
                                        border: '2px solid #e0e0e0',                                      
                                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        cursor: 'pointer',
                                        overflow: 'visible',
                                        position: 'relative',
                                        '&:hover': {
                                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                                            transform: 'translateY(-4px)',
                                            borderColor: currentAssessmentType.color,
                                        },
                                    }}
                                >
                                    <CardContent sx={{ p: 4 }}>
                                        {/* Card Header */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <Box>
    <Typography
        variant="body2"
        sx={{
            color: '#64748b',
            fontSize: '0.9rem'
        }}
    >
        {currentAssessmentType.label}
    </Typography>

    <Typography
        variant="h5"
        sx={{
            color: '#0B2D6B',
            fontWeight: 600
        }}
    >
        Assessment {assessment.cycle}
    </Typography>

    {selectedAssessment === 'subjective' && (
        <Typography
            sx={{
                mt: 1,
                color: '#475569',
                fontWeight: 600,
                fontSize: '0.95rem'
            }}
        >
            Total Score: {assessment.score} / 174
        </Typography>
    )}
</Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Typography variant="body1" sx={{ color: '#64748b', fontSize: '0.95rem' }}>
                                                    {new Date(assessment.date).toLocaleDateString()}
                                                </Typography>
                                                <Chip
                                                    label={tier.label}
                                                    sx={{
                                                        backgroundColor: `${tier.color}20`,
                                                        color: tier.color,
                                                        fontWeight: 600,
                                                        fontSize: '0.875rem',
                                                        height: 32,
                                                        px: 2,
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        {/* Expanded Details */}
                                       
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Box>
                )}
            </Container>
            </Box>
        </>
    );
};

export default AssessmentInsight;